import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import OfferModal from "@/components/admin/OfferModal";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

const WHEEL_COLORS = ["#1E5FA8", "#2B8FD4", "#F5A623", "#27AE60", "#E74C3C", "#8E44AD", "#16A085"];

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const { lang } = useApp();
  const tr = t[lang];

  const offerTypeLabels = {
    discount_percent: lang === "ar" ? "خصم نسبة" : "% Discount",
    discount_amount: lang === "ar" ? "خصم مبلغ" : "Fixed Discount",
    free_service: lang === "ar" ? "خدمة مجانية" : "Free Service",
    free_delivery: lang === "ar" ? "توصيل مجاني" : "Free Delivery",
    no_luck: lang === "ar" ? "حظ أوفر" : "No Luck",
  };

  useEffect(() => { loadOffers(); }, []);

  const loadOffers = async () => {
    setLoading(true);
    const list = await base44.entities.Offer.list();
    setOffers(list);
    setLoading(false);
  };

  const handleSave = async (offerData) => {
    if (editingOffer) {
      await base44.entities.Offer.update(editingOffer.id, offerData);
    } else {
      await base44.entities.Offer.create(offerData);
    }
    setShowModal(false);
    setEditingOffer(null);
    loadOffers();
  };

  const handleDelete = async (id) => {
    if (confirm(lang === "ar" ? "هل أنت متأكد من حذف هذا العرض؟" : "Are you sure you want to delete this offer?")) {
      await base44.entities.Offer.delete(id);
      loadOffers();
    }
  };

  const handleToggle = async (offer) => {
    await base44.entities.Offer.update(offer.id, { is_active: !offer.is_active });
    loadOffers();
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setShowModal(true);
  };

  const totalWeight = offers.filter(o => o.is_active).reduce((s, o) => s + (o.weight || 0), 0);

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-foreground">{tr.offerManagement}</h1>
              <p className="text-muted-foreground text-sm">
                {offers.length} {lang === "ar" ? "عرض إجمالاً" : "total offers"}
              </p>
            </div>
            <button
              onClick={() => { setEditingOffer(null); setShowModal(true); }}
              className="px-5 py-2.5 bg-primary text-primary-foreground font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-2"
            >
              ➕ {tr.addOffer}
            </button>
          </div>

          {/* Weight Info */}
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 text-sm">
            <p className="font-medium text-primary">
              📊 {lang === "ar" ? "إجمالي أوزان العروض المفعّلة:" : "Total active offers weight:"} <strong>{totalWeight}</strong> — {lang === "ar" ? "كلما زاد الوزن، زادت احتمالية الفوز بالعرض." : "Higher weight = higher probability of winning."}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid gap-4">
              {offers.map((offer, idx) => {
                const displayTitle = (lang === "en" && offer.title_en) ? offer.title_en : offer.title;
                const displayDesc = (lang === "en" && offer.description_en) ? offer.description_en : offer.description;
                return (
                  <div key={offer.id} className={`bg-card rounded-2xl border border-border p-5 shadow-sm transition-opacity ${!offer.is_active ? "opacity-60" : ""}`}>
                    <div className="flex items-start gap-4">
                      <div
                        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-sm shadow-md"
                        style={{ backgroundColor: WHEEL_COLORS[idx % WHEEL_COLORS.length] }}
                      >
                        {idx + 1}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-black text-foreground text-lg">{displayTitle}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${offer.is_active ? "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400" : "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400"}`}>
                            {offer.is_active ? `✅ ${tr.active}` : `❌ ${tr.inactive}`}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground font-medium">
                            {offerTypeLabels[offer.offer_type] || offer.offer_type}
                          </span>
                        </div>

                        {displayDesc && <p className="text-sm text-muted-foreground mb-2">{displayDesc}</p>}

                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span>⚖️ {tr.weight}: <strong className="text-foreground">{offer.weight || 0}</strong></span>
                          {offer.discount_value > 0 && (
                            <span>💰 {lang === "ar" ? "الخصم:" : "Discount:"} <strong className="text-foreground">{offer.discount_value}{offer.offer_type === "discount_percent" ? "%" : ` ${lang === "ar" ? "ر.س" : "SAR"}`}</strong>
                              {offer.offer_type === "discount_percent" && offer.max_discount_amount > 0 && (
                                <span className="text-amber-600 dark:text-amber-400"> (حد أقصى {offer.max_discount_amount} ر.س)</span>
                              )}
                            </span>
                          )}
                          {offer.min_invoice_amount > 0 && <span>🧾 {lang === "ar" ? "حد أدنى:" : "Min:"} <strong className="text-foreground">{offer.min_invoice_amount} {lang === "ar" ? "ر.س" : "SAR"}</strong></span>}
                          {offer.validity_days && <span>📅 {lang === "ar" ? "الصلاحية:" : "Validity:"} <strong className="text-foreground">{offer.validity_days} {lang === "ar" ? "يوم" : "days"}</strong></span>}
                          {offer.daily_win_limit > 0 && <span>📆 {lang === "ar" ? "حد يومي:" : "Daily:"} <strong className="text-foreground">{offer.daily_win_limit}</strong></span>}
                          {offer.monthly_win_limit > 0 && <span>📅 {lang === "ar" ? "حد شهري:" : "Monthly:"} <strong className="text-foreground">{offer.monthly_win_limit}</strong></span>}
                          {offer.total_win_limit > 0 && <span>🎯 {lang === "ar" ? "حد الحملة:" : "Campaign:"} <strong className="text-foreground">{offer.total_win_limit}</strong></span>}
                        </div>

                        {totalWeight > 0 && offer.is_active && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-muted-foreground">{tr.probability}</span>
                              <span className="font-bold" style={{ color: WHEEL_COLORS[idx % WHEEL_COLORS.length] }}>
                                {Math.round(((offer.weight || 0) / totalWeight) * 100)}%
                              </span>
                            </div>
                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${Math.round(((offer.weight || 0) / totalWeight) * 100)}%`,
                                  backgroundColor: WHEEL_COLORS[idx % WHEEL_COLORS.length]
                                }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <button onClick={() => handleEdit(offer)} className="px-3 py-1.5 bg-secondary text-secondary-foreground text-xs font-medium rounded-lg hover:bg-secondary/80 transition-colors">
                          ✏️ {tr.edit}
                        </button>
                        <button onClick={() => handleToggle(offer)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${offer.is_active ? "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 hover:bg-amber-200" : "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 hover:bg-green-200"}`}>
                          {offer.is_active ? (lang === "ar" ? "⏸ إيقاف" : "⏸ Pause") : (lang === "ar" ? "▶️ تفعيل" : "▶️ Activate")}
                        </button>
                        <button onClick={() => handleDelete(offer.id)} className="px-3 py-1.5 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors">
                          🗑 {tr.delete}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {offers.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <div className="text-5xl mb-4">🎁</div>
                  <p className="font-medium">{tr.noOffersList}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {showModal && (
          <OfferModal
            offer={editingOffer}
            onSave={handleSave}
            onClose={() => { setShowModal(false); setEditingOffer(null); }}
          />
        )}
      </AdminLayout>
    </AdminGuard>
  );
}